/**
 * Binary Format List Component
 *
 * Displays a table of binary format definitions with actions.
 */

import React, { useState } from 'react';
import {
  Edit,
  Trash2,
  Copy,
  Play,
  Archive,
  MoreHorizontal,
  FileCode,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import type { BinaryFormat } from '@/types/binaryFormat';

interface BinaryFormatListProps {
  formats: BinaryFormat[];
  systemTemplates: BinaryFormat[];
  isLoading: boolean;
}

export const BinaryFormatList: React.FC<BinaryFormatListProps> = ({
  formats,
  systemTemplates,
  isLoading,
}) => {
  const {
    openEditor,
    deleteFormat,
    publishFormat,
    deprecateFormat,
    cloneTemplate,
  } = useBinaryFormatStore();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formatToDelete, setFormatToDelete] = useState<BinaryFormat | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleEdit = (format: BinaryFormat) => {
    openEditor(format);
  };

  const handleDelete = (format: BinaryFormat) => {
    setFormatToDelete(format);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (formatToDelete) {
      setActionLoading(formatToDelete._id);
      await deleteFormat(formatToDelete._id);
      setActionLoading(null);
      setDeleteDialogOpen(false);
      setFormatToDelete(null);
    }
  };

  const handlePublish = async (format: BinaryFormat) => {
    setActionLoading(format._id);
    await publishFormat(format._id);
    setActionLoading(null);
  };

  const handleDeprecate = async (format: BinaryFormat) => {
    setActionLoading(format._id);
    await deprecateFormat(format._id);
    setActionLoading(null);
  };

  const handleClone = async (template: BinaryFormat) => {
    setActionLoading(template._id);
    await cloneTemplate(template._id, `${template.name} (Copy)`);
    setActionLoading(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'active':
        return <Badge variant="default" className="bg-green-600">Active</Badge>;
      case 'deprecated':
        return <Badge variant="outline" className="text-muted-foreground">Deprecated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading && formats.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Templates Section */}
      {systemTemplates.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              System Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {systemTemplates.map((template) => (
                <div
                  key={template._id}
                  className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleClone(template)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileCode className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm truncate">{template.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {template.description || `${template.fields.length} fields`}
                  </p>
                  {actionLoading === template._id && (
                    <Loader2 className="h-4 w-4 animate-spin mt-2" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Formats Table */}
      {formats.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <FileCode className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No binary formats yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first binary format to start parsing IoT payloads
          </p>
          <Button onClick={() => openEditor()}>
            Create Format
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Input Format</TableHead>
              <TableHead>Fields</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-[70px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {formats.map((format) => (
              <TableRow key={format._id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{format.name}</div>
                    {format.description && (
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {format.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">
                    {format.inputFormat.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell>{format.fields.length}</TableCell>
                <TableCell>{getStatusBadge(format.status)}</TableCell>
                <TableCell>v{format.version}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDate(format.updatedAt)}
                </TableCell>
                <TableCell>
                  {actionLoading === format._id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(format)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleClone(format)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Clone
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {format.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handlePublish(format)}>
                            <Play className="h-4 w-4 mr-2" />
                            Publish
                          </DropdownMenuItem>
                        )}
                        {format.status === 'active' && (
                          <DropdownMenuItem onClick={() => handleDeprecate(format)}>
                            <Archive className="h-4 w-4 mr-2" />
                            Deprecate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(format)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Binary Format</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{formatToDelete?.name}"? This action cannot be undone.
              {formatToDelete?.status === 'active' && (
                <span className="block mt-2 text-destructive">
                  Warning: This format is currently active and may be in use by workflows.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BinaryFormatList;
